import { formatDistanceToNowStrict } from 'date-fns';
import type { FollowupRow, FollowupState, FollowupView } from './types';

/** Every hold and end reason the backend sends, in the words a Sentinel reads. One place, so the chips, filter and timeline agree. */
export const FOLLOWUP_REASON_LABELS: Record<string, string> = {
  // Holds
  outside_window: 'Outside support hours',
  handoff_open: 'A supervisor decision is open',
  recent_automated_message: 'Another automated message went out recently',
  other_ticket_reply: 'The customer replied on another ticket',
  // Ends
  customer_reply: 'Customer replied',
  human_reply: 'An agent replied',
  closed: 'Ticket closed',
  assigned: 'Ticket assigned to an agent',
  opt_out: 'Customer opted out of follow-ups',
  brand_off: 'Follow-ups turned off for this brand',
  discarded_by_sentinel: 'Cancelled by a Sentinel',
  hold_ceiling: 'Held for too long',
  handoff: 'Handed to a person',
  completed: 'Completed',
  superseded: 'Replaced by a newer follow-up',
  unmatched_workflow: 'No workflow matched this wait',
  invalid: 'The wait program is invalid',
  duplicate: 'This update was already sent',
  dropped_by_brahma: 'No follow-up was needed',
  brahma_cannot_continue: 'Brahma had no open conversation to continue',
  max_age: 'Reached the 30-day limit',
  system_error: 'Stopped after repeated system errors',
  auto_reply: 'Automatic reply (out-of-office or acknowledgement)',
  nudge_limit: 'Follow-up limit reached for this customer',
};

/** Timeline-only: why a step is being retried. Not a state a follow-up can be filtered by. */
const RETRY_REASON_LABELS: Record<string, string> = {
  brahma_error: 'Drafting failed',
  brahma_empty: 'Drafting returned nothing',
  brahma_no_response: 'No draft came back',
  action_failed: 'A lookup failed',
  fire_failed: 'A system error occurred',
};

export const HOLD_REASONS = ['outside_window', 'handoff_open', 'recent_automated_message', 'other_ticket_reply'];

/** A code the map does not know yet still reads as words, never as snake_case. */
export const followupReasonLabel = (code: string): string => {
  const label = FOLLOWUP_REASON_LABELS[code] ?? RETRY_REASON_LABELS[code];
  if (label) return label;
  const words = code.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code;
};

export const FOLLOWUP_VIEW_LABELS: Record<FollowupView, string> = {
  due: 'Due in 24h',
  waiting: 'Waiting',
  held: 'Held',
  drafts: 'Drafts to review',
  handoff: 'Needs a person',
  ended: 'Ended (7 days)',
};

export const FOLLOWUP_STATE_LABELS: Record<FollowupState, string> = {
  waiting: 'Waiting',
  held: 'Held',
  queued: 'Draft ready',
  sending: 'Sending',
  ended: 'Ended',
};

/** The state chip's words, with the reason when there is one. */
export const followupStateText = (row: Pick<FollowupRow, 'state' | 'holdReason' | 'endReason' | 'draft'>): string => {
  const reason = row.state === 'held' ? row.holdReason : row.state === 'ended' ? row.endReason : null;
  // Queued with no draft yet: Kim has asked Brahma and is waiting for the draft.
  const state =
    row.state === 'queued' && !row.draft
      ? 'Drafting'
      : row.state === 'queued' && row.draft?.blocked
        ? 'Blocked — needs you'
        : (FOLLOWUP_STATE_LABELS[row.state] ?? row.state);
  return reason ? `${state}: ${followupReasonLabel(reason)}` : state;
};

/** The header control's words for a live follow-up. */
export function followupHeaderLabel(row: FollowupRow, now: Date = new Date()): string {
  if (row.state === 'queued') return row.draft?.blocked ? 'Follow-up blocked' : row.draft ? 'Draft ready' : 'Drafting follow-up';
  if (row.state === 'held') return 'Held';
  if (row.state === 'sending') return 'Sending';
  const due = row.nextDueAt ? new Date(row.nextDueAt) : null;
  if (!due || Number.isNaN(due.getTime()) || due.getTime() <= now.getTime()) return 'Follow-up due';
  return `Follow-up ${formatDistanceToNowStrict(due, { addSuffix: true })}`;
}
